import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { getLessons, createLesson, updateLesson, deleteLesson } from '../../apis/lessons';
import { RootState } from '../store';
import { Media } from '../../types/media';

export interface Lesson {
    id: string;
    title: string;
    group_name?: string | null;
    content?: string;
    level?: number;
    banner_url?: string;
    category_id: string;
    is_public?: boolean;
    created_by_id?: string;
    created_at: string;
    updated_at: string;
    category?: {
        id: string;
        name: string;
        teacher_id: string;
        teacher?: {
            id: string;
            full_name: string;
            email: string;
        }
    };
    creator?: {
        id: string;
        full_name: string;
        email: string;
    };
    student_count?: number;
    view_count?: number;
    deleted_at?: string | null;
    videos?: Media[];
    documents?: Media[];
    clone_from_lesson_id?: string;
    cloned_id?: string;
    book_pages?: any[];
    organization_id?: string | null;
    organization_name?: string | null;
}

interface LessonState {
    lessons: Lesson[];
    total: number;
    page: number;
    size: number;
    pages: number;
    loading: boolean;
    error: string | null;
}

const initialState: LessonState = {
    lessons: [],
    total: 0,
    page: 1,
    size: 12,
    pages: 1,
    loading: false,
    error: null,
};

// Thunks
export const fetchLessons = createAsyncThunk(
    'lessons/fetchLessons',
    async (params: Record<string, any> = {}, { rejectWithValue }) => {
        try {
            const response = await getLessons(params);
            return response.data;
        } catch (err: any) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

export const addLesson = createAsyncThunk(
    'lessons/addLesson',
    async (data: Partial<Lesson>, { rejectWithValue }) => {
        try {
            const response = await createLesson(data);
            return response.data;
        } catch (err: any) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

export const editLesson = createAsyncThunk(
    'lessons/editLesson',
    async ({ id, data }: { id: string; data: Partial<Lesson> }, { rejectWithValue }) => {
        try {
            const response = await updateLesson(id, data);
            return response.data;
        } catch (err: any) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

export const removeLesson = createAsyncThunk(
    'lessons/removeLesson',
    async (id: string, { rejectWithValue }) => {
        try {
            await deleteLesson(id);
            return id;
        } catch (err: any) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

const lessonSlice = createSlice({
    name: 'lessons',
    initialState,
    reducers: {
        setPage: (state, action: PayloadAction<number>) => {
            state.page = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchLessons.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchLessons.fulfilled, (state, action: PayloadAction<any>) => {
                state.loading = false;
                state.lessons = action.payload.items;
                state.total = action.payload.total;
                state.page = action.payload.page;
                state.size = action.payload.size;
                state.pages = action.payload.pages;
            })
            .addCase(fetchLessons.rejected, (state, action: any) => {
                state.loading = false;
                const pl = action.payload;
                state.error = pl?.detail
                    ? (typeof pl.detail === 'string' ? pl.detail : JSON.stringify(pl.detail))
                    : (typeof pl === 'string' ? pl : 'An error occurred while fetching lessons');
            })
            .addCase(addLesson.fulfilled, (state, action: PayloadAction<Lesson>) => {
                state.lessons.unshift(action.payload);
                state.total += 1;
            })
            .addCase(editLesson.fulfilled, (state, action: PayloadAction<Lesson>) => {
                const index = state.lessons.findIndex((l) => l.id === action.payload.id);
                if (index !== -1) state.lessons[index] = action.payload;
            })
            .addCase(removeLesson.fulfilled, (state, action: PayloadAction<string>) => {
                state.lessons = state.lessons.filter((l) => l.id !== action.payload);
                state.total -= 1;
            });
    },
});

export const { setPage } = lessonSlice.actions;

export const selectAllLessons = (state: RootState) => state.lessons.lessons;
export const selectLessonById = (state: RootState, id: string) =>
    state.lessons.lessons.find((l) => l.id === id);

export default lessonSlice.reducer;
